import {shutdown as shutdownIntercom} from '@intercom/messenger-js-sdk';
import * as Sentry from '@sentry/react';

import * as authApi from '~api/auth';
import {loginRedirectQueryParam, loginStatusQueryParam, replaceRoute, Route} from '~utils/routeUtil';
import {resetToInitialState} from '~redux/actions/globalActions';
import {store} from '~redux/index';

import {clearStoredPanel} from '~components/ask-ai/askAiPanelStorage';
import {queryClient} from 'src/queryClient';
import {abortAllRequests} from './api';

export const TOKEN_STORAGE_KEY = 'token';

const TOKEN_ID = TOKEN_STORAGE_KEY;

function getToken(): string | null {
  return localStorage.getItem(TOKEN_ID);
}

function setToken(token: string): void {
  localStorage.setItem(TOKEN_ID, token);
}

async function logoutCleanup(): Promise<void> {
  // Cleanup local data
  clearToken();
  clearStoredPanel();
  queryClient.removeQueries();
  store.dispatch(resetToInitialState());

  if (typeof window !== 'undefined' && typeof (window as any).Intercom === 'function') {
    shutdownIntercom();
  }

  await abortAllRequests();
  Sentry.setUser(null);
}

function clearToken(): void {
  localStorage.removeItem(TOKEN_ID);
}

/**
 * @throws {Error} if the login fails
 */
async function login(email: string, password: string): Promise<void> {
  try {
    const {accessToken} = await authApi.login(email, password);
    if (!accessToken) {
      throw new Error('Login failed');
    }
    setToken(accessToken);
  } finally {
    Sentry.setUser({email});
  }
}

async function logout(options: {loginPageStatus?: string; redirectUrl?: string} = {}): Promise<void> {
  // If already on 503 page, skip API calls and redirects, just clean up Sentry
  if (window.location.href.includes('503')) {
    clearToken();
    Sentry.setUser(null);
    return;
  }

  try {
    // Get logout URL from backend and redirect to it
    const {logoutUrl} = await authApi.getLogoutUrl();

    // Mark that we're in a logout flow so callback handler doesn't auto-login
    // This prevents WorkOS from redirecting back with a token and re-authenticating
    sessionStorage.setItem('logout_in_progress', 'true');

    // Clear token and cleanup right before redirecting to WorkOS
    await logoutCleanup();
    window.location.replace(logoutUrl);
  } catch (e: any) {
    await logoutCleanup();
    // If getting logout URL fails, fallback to index page redirect
    await replaceRoute(Route.index, {
      query: {
        [loginStatusQueryParam]: options.loginPageStatus ?? 'logout',
        [loginRedirectQueryParam]: options.redirectUrl,
      },
      keepProjectIdQueryParam: false,
      keepOrgIdQueryParam: false,
      keepModelIdQueryParam: false,
    });
  }
}

function isLoggedIn(): boolean {
  return getToken() !== null;
}

export const AuthService = {
  login,
  logout,
  isLoggedIn,
  getToken,
  setToken,
};
